"use client"

import Link from "next/link"
import { ArrowUpRight } from "lucide-react"
import { useTranslations } from "next-intl"

const platformKeys = ["madeira", "kirbe", "envoy"] as const
const platformLinks = ["/manifiesto/madeira-agent", "/manifiesto/kirbe", "/manifiesto/envoy"]

// Define features arrays for each platform
const platformFeatures: Record<string, string[]> = {
  madeira: ["Natural language interface", "Multi-tool orchestration", "Contextual memory"],
  kirbe: ["Real-time data sync", "Dashboard analytics", "Resource management"],
  envoy: ["Secure communication", "End-to-end encryption", "Multi-channel support"],
}

export function PlatformsSection() {
  const t = useTranslations("techLab.platforms")
  
  return (
    <section className="py-24">
      {/* Section header */}
      <div className="text-center mb-16">
        <p className="text-slate-400 text-sm font-medium uppercase tracking-widest mb-3">
          {t("sectionLabel")}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          {t("title")}
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          {t("subtitle")}
        </p>
      </div>

      {/* Platform cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {platformKeys.map((key, idx) => {
          const link = platformLinks[idx]
          
          return (
            <div
              key={idx}
              className="group relative p-8 rounded-2xl bg-card/30 border border-border/50 hover:border-slate-400/30 transition-all duration-300"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-foreground">{t(`${key}.name`)}</h3>
                  <p className="text-slate-400 text-sm">{t(`${key}.tagline`)}</p>
                </div>
                <Link 
                  href={link}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ArrowUpRight className="w-5 h-5 text-muted-foreground" />
                </Link>
              </div>

              {/* Description */}
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t(`${key}.description`)}
              </p>

              {/* Features */}
              <ul className="space-y-2">
                {platformFeatures[key].map((feature, fIdx) => (
                  <li key={fIdx} className="flex items-center gap-2 text-sm text-muted-foreground/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400/60" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Read more */}
              <Link 
                href={link}
                className="inline-flex items-center gap-1 mt-6 text-sm font-medium text-slate-400 hover:gap-2 transition-all"
              >
                {t("learnMore")}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          )
        })}
      </div>
    </section>
  )
}
